import { z } from 'zod'

import { funeralHallSummary } from './funeralHall.js'

import { occasionType } from './product.js'

/**
 * 링크 파싱 계약 (BIZ-79 → BIZ-107 범용화). URL을 붙여넣으면 서버가 받아와 파싱해 주문을
 * 미리 채운다. 근조(부고)뿐 아니라 청첩장 등 occasion을 자동 판별한다.
 * 파싱·네트워크·화이트리스트는 서버가 처리(SSRF 방지). 클라이언트는 이 계약만 본다.
 *
 * 호환: 기존 부고 전용 이름(parseObituary…·ParsedObituary…)은 별칭으로 유지한다 — 계약을
 * 상위호환(superset)으로 넓혀 기존 소비자(web BIZ-79 흐름)가 그대로 컴파일된다. 범용 입력
 * UI(BIZ-106)는 새 이름(parseLink…·ParsedLink…)으로 이관한다.
 */
export const parseLinkRequest = z.object({
  /**
   * 붙여넣은 링크 또는 텍스트(BIZ-109). 카톡 공유는 안내문구가 섞이고 원본 URL이 단축/래핑되므로
   * 엄격한 URL 검증 대신 텍스트를 받아 서버가 URL을 추출·리다이렉트 해석한다. 필드명은 web 호환 유지.
   */
  url: z.string().min(1, { message: '링크를 입력하세요' }).max(2000),
})
export type ParseLinkRequest = z.infer<typeof parseLinkRequest>

/** 필드별 신뢰도. low면 화면에서 '확인 필요' 배지 + 해결 전 다음 비활성. */
export const linkFieldSchema = z.object({
  value: z.string(),
  confidence: z.enum(['high', 'low']),
})
export type LinkFieldDto = z.infer<typeof linkFieldSchema>

/**
 * occasion별 프리필 필드. 파서는 관련 필드만 채운다(전부 optional). 공통 3개는 occasion 무관,
 * 나머지는 근조/청첩장 전용. 키는 주문 컬럼과 정합(delivery_place_name·event_target 등)해
 * 프론트가 occasion별 폼으로 바로 매핑한다.
 */
export const parsedLinkFields = z.object({
  // 공통 (근조·청첩장 공유)
  deliveryPlaceName: linkFieldSchema.optional(), // 장례식장명 / 예식장명
  deliveryAddress: linkFieldSchema.optional(),
  desiredDate: linkFieldSchema.optional(), // 발인 / 예식일 기준 희망 배송일(ISO yyyy-mm-dd)
  // 근조(FUNERAL)
  deceasedName: linkFieldSchema.optional(),
  mournerName: linkFieldSchema.optional(),
  mortuaryRoom: linkFieldSchema.optional(),
  // 청첩장(CELEBRATION)
  eventTarget: linkFieldSchema.optional(), // 주인공(신랑·신부) "○○ · ○○"
  eventName: linkFieldSchema.optional(), // 예식명(예: 결혼식)
  eventType: linkFieldSchema.optional(), // 행사 종류(예: 결혼, 돌잔치, 개업) — 주문 event_type
  eventDateTime: linkFieldSchema.optional(), // 예식 일시(ISO)
})
export type ParsedLinkFields = z.infer<typeof parsedLinkFields>

export const parsedLinkSchema = z.object({
  /** 어느 파서로 뽑았는지 (전용 파서 이름 또는 'llm') */
  source: z.string(),
  /** 자동 판별한 occasion. GIFT는 현재 파서 대상 아님(스키마만 공유). */
  occasionType,
  /** occasion 판별 신뢰도. low면 화면에서 사용자가 종류를 확인/변경하게 한다. */
  occasionConfidence: z.enum(['high', 'low']),
  fields: parsedLinkFields,
  /** 발송 불가 등 사용자에게 보여줄 경고 (예: 이미 발인됨) */
  warnings: z.array(z.string()),
  /** 장소명을 명부와 연결한 결과(BIZ-90). 못 찾으면 null. 주문서가 funeralHallId·제휴 판정에 쓴다. */
  funeralHall: funeralHallSummary.nullable().optional(),
})
export type ParsedLinkDto = z.infer<typeof parsedLinkSchema>

export const parseLinkResponse = z.object({ parsed: parsedLinkSchema })
export type ParseLinkResponse = z.infer<typeof parseLinkResponse>

export const LinkErrorCode = {
  VALIDATION_FAILED: 'VALIDATION_FAILED',
  /** 전용 파서가 없는 사이트 (LLM 폴백 미구현) */
  UNSUPPORTED_HOST: 'UNSUPPORTED_HOST',
  /** 페이지를 받아오지 못함 */
  FETCH_FAILED: 'FETCH_FAILED',
} as const

// ── 하위호환 별칭 (부고 전용 이름 → 범용) ─────────────────────────
// 기존 BIZ-79 소비자가 그대로 컴파일되도록 유지. 신규 코드는 위 범용 이름을 쓴다.
export const parseObituaryRequest = parseLinkRequest
export type ParseObituaryRequest = ParseLinkRequest
export const obituaryFieldSchema = linkFieldSchema
export type ObituaryFieldDto = LinkFieldDto
export const parsedObituarySchema = parsedLinkSchema
export type ParsedObituaryDto = ParsedLinkDto
export const parseObituaryResponse = parseLinkResponse
export type ParseObituaryResponse = ParseLinkResponse
export const ObituaryErrorCode = LinkErrorCode
